import { Slot } from '@radix-ui/react-slot';
import React, { memo, PropsWithChildren } from 'react';
import { identifyComponent } from '../../util';
import { useTooltip } from './hooks';
import { TooltipPopover } from './TooltipPopover';
import { TooltipProps } from './types';

const DEFAULT_TEST_ID = 'tooltip';

/**
 * Tooltips provide a way to show extra information when the content is hovered over with a mouse.
 * */
export const Tooltip = memo<PropsWithChildren<TooltipProps>>(function Tooltip({
  testId = DEFAULT_TEST_ID,
  anchor,
  children,
  persistDelay,
  showDelay,
  disabled,
  className,
  interactive = true,
  compact,
  asChild = false,
  childrenOverflowHidden,
  ...rest
}) {
  const { anchorRef, anchorEvents, hideTooltip, persistTooltip, visible } =
    useTooltip<HTMLDivElement>({ disabled, showDelay, persistDelay });
  const Component = asChild ? Slot : 'div';

  const interactiveProps: React.ComponentPropsWithoutRef<'div'> = interactive
    ? { role: 'button', tabIndex: 0 }
    : {};

  return (
    <>
      <Component
        ref={anchorRef}
        data-testid={testId}
        className={className}
        {...interactiveProps}
        {...anchorEvents}
        style={{ overflow: childrenOverflowHidden ? 'hidden' : 'visible' }}
      >
        {children}
      </Component>
      <TooltipPopover
        anchor={anchor ?? anchorRef.current}
        open={visible}
        hideTooltip={hideTooltip}
        persistTooltip={persistTooltip}
        compact={compact}
        {...rest}
        {...identifyComponent('tooltip', rest)}
      />
    </>
  );
});

/**
 * If the tooltip is going to be used with SVGs, you can use a `SVGTooltip`. Note that
 * this does wrap the underying content in a `g` element, so it can capture the events
 * from all it's children.
 */
export const SVGTooltip = memo<Omit<TooltipProps, 'tag'>>(function SVGTooltip({
  testId = DEFAULT_TEST_ID,
  children,
  persistDelay,
  showDelay,
  disabled,
  ...rest
}) {
  const { anchorRef, anchorEvents, hideTooltip, persistTooltip, visible } = useTooltip<SVGGElement>(
    { disabled, showDelay, persistDelay },
  );

  return (
    <>
      <g ref={anchorRef} data-testid={testId} {...anchorEvents}>
        {children}
      </g>
      <foreignObject>
        <TooltipPopover
          anchor={anchorRef.current}
          open={visible}
          hideTooltip={hideTooltip}
          persistTooltip={persistTooltip}
          {...rest}
        />
      </foreignObject>
    </>
  );
});
